import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

interface CookieOptions {
    expires?: DateTime | Date | number;
    path?: string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
    domain?: string;
}

@Injectable({ providedIn: 'root' })
export class CookiesService {
    /**
     * Get a cookie by name
     * @param name The name of the cookie to retrieve
     * @returns The cookie value or null if not found
     */
    get(name: string): string | null {
        const cookies = document.cookie.split(';');

        for (const cookie of cookies) {
            const [cookieName, cookieValue] = cookie.trim().split('=');
            if (cookieName === name) {
                return decodeURIComponent(cookieValue);
            }
        }

        return null;
    }

    getBoolean(name: string): boolean|null {
        const value = this.get(name);

        if (value !== null) {
            return value.toLowerCase() === 'true';
        }

        return null;
    }

    /**
     * Set a cookie with the provided name and value
     * @param name The name of the cookie
     * @param value The value to store
     * @param options Optional settings for the cookie
     */
    set(name: string, value: string | boolean, options: CookieOptions = {}): void {
        if (value === true) {
            value = 'true';
        } else if (value === false) {
            value = 'false';
        }

        let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        if (options.expires) {
            if (typeof options.expires === 'number') {
                const days = options.expires;
                const date = new Date();
                date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
                cookieString += `; expires=${date.toUTCString()}`;
            }
            if (options.expires instanceof DateTime) {
                const date = options.expires.toJSDate();

                cookieString += `; expires=${date.toUTCString()}`;
            } else if (options.expires instanceof Date) {
                cookieString += `; expires=${options.expires.toUTCString()}`;
            } else {
                throw new Error(`Invalid expires value: ${options.expires}`);
            }
        }

        if (options.path) {
            cookieString += `; path=${options.path}`;
        } else {
            cookieString += '; path=/';
        }

        if (options.domain) {
            cookieString += `; domain=${options.domain}`;
        }

        if (options.secure) {
            cookieString += '; secure';
        }

        if (options.sameSite) {
            cookieString += `; samesite=${options.sameSite}`;
        }

        document.cookie = cookieString;
    }

    /**
     * Check if a cookie exists
     * @param name The name of the cookie to check
     * @returns True if the cookie exists
     */
    has(name: string): boolean {
        return this.get(name) !== null;
    }

    /**
     * Delete a cookie by setting its expiration to the past
     * @param name The name of the cookie to delete
     * @param path The path of the cookie (must match the path used when setting)
     */
    delete(name: string, path: string = '/'): void {
        this.set(name, '', {
            expires: new Date(0),
            path
        });
    }

    /**
     * Get all cookies as an object
     * @returns An object containing all cookies
     */
    getAll(): Record<string, string> {
        const result: Record<string, string> = {};
        const cookies = document.cookie.split(';');

        for (const cookie of cookies) {
            if (cookie.trim()) {
                const [name, value] = cookie.trim().split('=');
                result[name] = decodeURIComponent(value);
            }
        }

        return result;
    }
}
